import { useEffect, useRef, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";

// =============================================================================
// PdfReader — a PDF drawn INTO the page, sized to the width, scrolled naturally
// (3 Oct 2026, for the policy reader; reused by HSE alerts and toolbox packs)
// =============================================================================
// James, 3 Oct 2026, on the old policy reader: "either click to go to a PDF tab
// then the user has to go back on the web browser to get back to acknowledge,
// or you view in the app and it's not sized right and scrolls weird."
//
// Both complaints are the <iframe>: iOS renders a PDF inside an iframe at page
// width with no pinch and its own scroll trap, and a new tab loses the
// Acknowledge button. So this component renders every page itself with
// pdf.js (Mozilla's renderer — what Firefox uses) onto canvases that are
// exactly the container's width, stacked top to bottom. The document becomes
// ordinary page content: the phone scrolls it like any other screen, the
// acknowledge bar can sit under it, and we can SEE when the reader reaches the
// last page (IntersectionObserver on the final canvas → onReachedEnd), which
// the iframe could never tell us.
//
// Bytes come from the caller as an ArrayBuffer (the api-gateway streams them
// through /files/:id/pdf-bytes with the user's JWT) — never a cross-origin R2
// URL, so no R2 CORS and no signed-URL expiry mid-read.
//
// pdf.js is ~1.3 MB with its worker, so it is `await import()`-ed on first use
// (same rule as pdfmake/exceljs — a static import blew the PWA precache on
// 2 Sep). The LEGACY build is used on purpose: the modern build needs
// Promise.withResolvers, which older iPads in the utes don't have.
//
// Re-renders on container resize (rotate the phone → pages redraw to the new
// width). Draws at devicePixelRatio so text is crisp on retina screens.
// =============================================================================

type Props = {
  /** The PDF bytes. Pass null while loading. */
  data: ArrayBuffer | null;
  /** Fires once, the first time the last page scrolls into view. */
  onReachedEnd?: () => void;
  /** Fires on every render with (visiblePage, pageCount). */
  onProgress?: (page: number, pageCount: number) => void;
  /** Max page width in CSS px on wide screens (default 900). */
  maxWidth?: number;
  className?: string;
};

type PdfJs = typeof import("pdfjs-dist/legacy/build/pdf.mjs");
let pdfjsPromise: Promise<PdfJs> | null = null;
async function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = await import("pdfjs-dist/legacy/build/pdf.mjs");
      const worker = await import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url");
      lib.GlobalWorkerOptions.workerSrc = worker.default;
      return lib;
    })();
  }
  return pdfjsPromise;
}

export function PdfReader({ data, onReachedEnd, onProgress, maxWidth = 900, className }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<any>(null);
  const [pageCount, setPageCount] = useState(0);
  const [width, setWidth] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const reachedEndRef = useRef(false);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);

  // 1. Open the document when bytes arrive.
  useEffect(() => {
    let cancelled = false;
    setDoc(null); setPageCount(0); setError(null); reachedEndRef.current = false;
    if (!data) return;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        // pdf.js takes ownership of the buffer — hand it a copy so a re-render
        // (StrictMode double-mount, resize) never sees a detached buffer.
        const task = pdfjs.getDocument({ data: data.slice(0) });
        const d = await task.promise;
        if (cancelled) { d.destroy(); return; }
        setDoc(d); setPageCount(d.numPages);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  // 2. Track the container width (rotation, sidebar, window resize).
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => setWidth(Math.min(maxWidth, Math.floor(el.clientWidth)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxWidth]);

  // 3. Draw every page at the current width × devicePixelRatio.
  useEffect(() => {
    if (!doc || !width) return;
    let cancelled = false;
    setRendering(true);
    (async () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      for (let i = 1; i <= doc.numPages; i++) {
        if (cancelled) return;
        const canvas = canvasRefs.current[i - 1];
        if (!canvas) continue;
        const page = await doc.getPage(i);
        const base = page.getViewport({ scale: 1 });
        const scale = width / base.width;
        const vp = page.getViewport({ scale: scale * dpr });
        canvas.width = Math.floor(vp.width);
        canvas.height = Math.floor(vp.height);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${Math.floor(vp.height / dpr)}px`;
        const ctx = canvas.getContext("2d");
        if (!ctx) continue;
        await page.render({ canvasContext: ctx, viewport: vp }).promise;
      }
      if (!cancelled) setRendering(false);
    })();
    return () => { cancelled = true; };
  }, [doc, width]);

  // 4. Which page is on screen, and have we reached the last one.
  useEffect(() => {
    if (!doc || pageCount === 0) return;
    const canvases = canvasRefs.current.slice(0, pageCount).filter(Boolean) as HTMLCanvasElement[];
    if (canvases.length === 0) return;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        const idx = canvases.indexOf(en.target as HTMLCanvasElement);
        if (idx >= 0) onProgress?.(idx + 1, pageCount);
        if (idx === pageCount - 1 && !reachedEndRef.current) {
          reachedEndRef.current = true;
          onReachedEnd?.();
        }
      }
    }, { threshold: 0.15 });
    canvases.forEach(cv => io.observe(cv));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, pageCount]);

  // A one-page document is "read to the end" the moment it is on screen —
  // the observer above handles that too (the only page is the last page).

  return (
    <div ref={hostRef} className={`w-full ${className ?? ""}`}>
      {error ? (
        <div className="p-6 text-text-muted text-sm flex items-start gap-2">
          <AlertCircle size={16} className="text-lost mt-0.5 shrink-0"/>
          <span>Couldn't draw the PDF here: <span className="font-mono">{error}</span></span>
        </div>
      ) : !data || !doc ? (
        <div className="p-10 flex items-center justify-center gap-2 text-text-muted text-[13px]"><Loader2 size={16} className="animate-spin"/> Loading the document…</div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-3">
          {Array.from({ length: pageCount }, (_, i) => (
            <canvas
              key={i}
              ref={el => { canvasRefs.current[i] = el; }}
              className="bg-white shadow-sm rounded-sm"
              style={{ width: width || "100%", maxWidth: "100%" }}
              aria-label={`Page ${i + 1} of ${pageCount}`}
            />
          ))}
          {rendering && <div className="text-[11px] text-text-muted flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> drawing pages…</div>}
        </div>
      )}
    </div>
  );
}

/** Fetch a PDF's bytes through the api-gateway (JWT-scoped, RLS-scoped). */
export async function fetchPdfBytes(apiFetch: (path: string, init?: RequestInit) => Promise<Response>, fileId: string): Promise<ArrayBuffer> {
  const r = await apiFetch(`/files/${encodeURIComponent(fileId)}/pdf-bytes`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({} as any));
    throw new Error((err as any)?.error ?? `pdf_fetch_failed (${r.status})`);
  }
  return r.arrayBuffer();
}
